import {
  AccountPageOrder,
  AccountPageOrderDetail,
  AccountPageOrderItem,
  AccountPageOrderStatus,
  OrderAddress,
  OrderTrackingInfo,
} from "@shopflo/ui/account-page";

const formatPrice = (amount: number, currency: string) =>
  currency === "INR" ? `₹${amount.toLocaleString("en-IN")}` : `${amount.toLocaleString()}`;

const formatOrderDate = (createdAt: string) =>
  new Date(createdAt).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

// An order counts as delivered only when at least one fulfillment reports
// display_status DELIVERED or PICKED_UP — display_fulfillment_status FULFILLED
// merely means the merchant shipped it, not that the customer received it.
export const mapOrderStatus = (o: any): AccountPageOrderStatus => {
  if (o?.cancelled) return "cancelled";
  const fin: string = o?.display_financial_status ?? "";
  if (fin === "PARTIALLY_REFUNDED" || fin === "REFUNDED") return "refunded";
  const isDelivered = (o?.fulfillments ?? []).some(
    (f: any) => f?.display_status === "DELIVERED" || f?.display_status === "PICKED_UP",
  );
  if (isDelivered) return "delivered";
  return "in_transit";
};

// Flatten every fulfillment's tracking_info into one list, dropping entries
// without a URL (nothing to open) and duplicates (same fulfillment can be
// repeated in the API response).
export const mapTrackingInfo = (fulfillments: any[]): OrderTrackingInfo[] => {
  const seen = new Set<string>();
  return (fulfillments ?? [])
    .flatMap((f: any) => f?.tracking_info ?? [])
    .filter((t: any) => {
      if (!t?.url) return false;
      const key = `${t.number ?? ""}|${t.url}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map((t: any) => ({
      number: t.number ?? "",
      url: t.url,
      company: t.company ?? "",
    }));
};

const mapOrderItems = (lineItems: any[]): AccountPageOrderItem[] =>
  lineItems.map((item: any) => ({
    id: item.variant_id ?? item.product_id,
    name: item.title,
    qty: item.quantity,
    variant: item.variant_title ?? null,
    price: formatPrice(parseFloat(item.discounted_price ?? "0"), item.currency_code ?? "INR"),
    imageUrl: item.image_url ?? undefined,
    productUrl: item.product_url ?? undefined,
  }));

const mapOrderAddress = (addr: any): OrderAddress | undefined =>
  !addr
    ? undefined
    : {
        firstName: addr.first_name ?? "",
        lastName: addr.last_name,
        address1: addr.address1 ?? "",
        address2: addr.address2,
        city: addr.city ?? "",
        province: addr.province ?? "",
        country: addr.country ?? "",
        zip: addr.zip ?? "",
        phone: addr.phone,
      };

export const mapRawOrder = (o: any): AccountPageOrder => {
  const lineItems: any[] = o.line_items ?? [];
  const total = lineItems.reduce(
    (sum: number, item: any) => sum + parseFloat(item.discounted_price ?? "0") * (item.quantity ?? 1),
    0,
  );
  const currency: string = lineItems[0]?.currency_code ?? "INR";
  return {
    id: o.id,
    orderNumber: o.name,
    date: formatOrderDate(o.created_at),
    cancelled: Boolean(o.cancelled),
    financialStatus: o.display_financial_status ?? "",
    fulfillmentStatus: o.display_fulfillment_status ?? "",
    status: mapOrderStatus(o),
    totalPrice: formatPrice(total, currency),
    items: mapOrderItems(lineItems),
    trackingInfo: mapTrackingInfo(o.fulfillments),
  };
};

// Full order detail (single-order API response): everything from the list
// mapping plus addresses, transactions and the order note.
export const mapRawOrderDetail = (o: any): AccountPageOrderDetail => ({
  ...mapRawOrder(o),
  shippingAddress: mapOrderAddress(o.shipping_address),
  billingAddress: mapOrderAddress(o.billing_address),
  transactions: (o.transactions ?? []).map((t: any) => {
    const amount = parseFloat(t.amount ?? "0");
    const currency: string = t.currency_code ?? "INR";
    return {
      id: t.id,
      kind: t.kind,
      status: t.status,
      gateway: t.gateway,
      amount: formatPrice(amount, currency),
      currencyCode: currency,
    };
  }),
  note: o.note,
});
